import { useIdentity } from '@equipmentshare/auth0-react'
import { useEffect } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import jwtDecode from 'jwt-decode'

// ES identity claims are namespaced (e.g. https://.../es_user_id), so match by suffix.
const findClaim = ( claims: Record<string, unknown>, suffix: string ): unknown => Object.entries( claims ).find( ( [ key ] ) => key.includes( suffix ) )?.[ 1 ]

type FormatRumUserParams = {
	userId: string | number,
	companyId: string | number,
	fullName: string,
	email: string,
	isMimicked: boolean,
	realUserId?: string | number,
	realUserName?: string,
	realUserEmail?: string,
	mimicId?: string,
}

// Inlined from @equipmentshare/audit-logger-ui to avoid its Node >=22 engine requirement
const formatRumUser = ( { userId, companyId, fullName, email, isMimicked, realUserId, realUserName, realUserEmail, mimicId }: FormatRumUserParams ) => {
	const base = {
		id: String( userId ),
		name: fullName,
		email,
		companyId: String( companyId ),
		isMimicked,
	}
	return isMimicked
		? {
			...base,
			realUserId: realUserId ? String( realUserId ) : undefined,
			realUserName,
			realUserEmail,
			mimicId,
		}
		: base
}

export const useDDRum = () => {
	const { userId, companyId, fullName, email, idToken } = useIdentity()
	useEffect( () => {
		// return if dd is not enabled
		if ( datadogRum.getInitConfiguration() === undefined ) {
			return
		}
		// clear before the idToken guard so a signed-out user's later activity is not still attributed to them
		datadogRum.clearUser()
		if ( !idToken ) {
			return
		}

		// Mimic attribution lives in the token claims, not in the useIdentity() body.
		const claims = jwtDecode<Record<string, unknown>>( idToken )
		const rawIsMimicked = findClaim( claims, 'is_mimicked' )

		datadogRum.setUser( formatRumUser( {
			userId,
			companyId,
			fullName,
			email,
			isMimicked: rawIsMimicked === true || rawIsMimicked === 'true',
			realUserId: findClaim( claims, 'real_user_id' ) as string | number | undefined,
			realUserName: findClaim( claims, 'real_user_name' ) as string | undefined,
			realUserEmail: findClaim( claims, 'real_user_email' ) as string | undefined,
			mimicId: findClaim( claims, 'mimic_id' ) as string | undefined,
		} ) )
		datadogRum.startSessionReplayRecording()


		// being pragmatic here, company name is not available in jwt
		// and we don't want the overhead of IO just for analytics
		const handle = setInterval( () => {
			// this is attempting to steal the current company name from the Unav Header
			const companyName = document.querySelector( '[data-testid="company-name"]' )?.textContent?.trim()
			if ( companyName ) {
				datadogRum.setUserProperty( 'companyName', companyName )
				clearInterval( handle )
			}
		}, 2000 )
	}, [ userId, companyId, fullName, email, idToken ] )
	return null
}
